import {
    Grid,
    HorizontalScroller,
    Stack,
    Tab,
    TabGroup,
    Tooltip,
    tabClasses,
} from "@biggeo/bg-ui/lab";
import * as A from "fp-ts/lib/Array";
import { pipe } from "fp-ts/lib/function";
import isEmpty from "lodash/isEmpty";
import startCase from "lodash/startCase";

import { MapLayoutTabs } from "../../../layouts/MapLayout";
import { MapTableTabs } from "../../map-wrappers/MapViewWrapper";
import { SetDatasetContextType } from "../../mapbox/context/utils/general-utils";
import { MapContextDataset } from "../../mapbox/context/types";
import { DataGridDivider } from "../components/DatasetTableHeader";
import FilterAppliedChip from "../components/FilterAppliedChip";
import MapFeedbackContainer from "../containers/MapFeedbackContainer";

export interface IMapTopbar
    extends Pick<SetDatasetContextType, "updateDataset"> {
    readonly currentTab: MapTableTabs;
    readonly setTab: (tab: MapTableTabs) => void;
    readonly datasets: MapContextDataset[];
    readonly deletePolygon: (id: string) => void;
    readonly setNavTab: (tab: MapLayoutTabs) => void;
}

const MapTopbar = ({
    currentTab,
    setTab,
    datasets,
    updateDataset,
    deletePolygon,
    setNavTab,
}: IMapTopbar) => {
    const filteredDatasets = pipe(
        datasets,
        A.filter((d) => d.isSelected && !isEmpty(d.filters.filters))
    );

    const selectedDatasets = pipe(
        datasets,
        A.filter((d) => d.isSelected)
    );

    const hasFilters = !pipe(filteredDatasets, isEmpty);

    return (
        <Grid
            container
            justifyContent="space-between"
            width="100%"
            sx={{
                padding: 2,
            }}
        >
            <Grid item minWidth={0}>
                <Stack flexDirection="row" gap={2}>
                    <HorizontalScroller>
                        <TabGroup
                            variant="tonal"
                            color="surface"
                            value={currentTab}
                            density="dense"
                        >
                            {Object.values(MapTableTabs).map((tab) => (
                                <Tooltip
                                    key={tab}
                                    title={
                                        isEmpty(datasets)
                                            ? "Enable datasets to view table"
                                            : "Select datasets to view table"
                                    }
                                    disableHover={
                                        tab === MapTableTabs.map ||
                                        !isEmpty(selectedDatasets)
                                    }
                                >
                                    <span>
                                        <Tab
                                            onClick={() => setTab(tab)}
                                            value={tab}
                                            disabled={
                                                (tab === MapTableTabs.table ||
                                                    tab ===
                                                        MapTableTabs.split) &&
                                                isEmpty(selectedDatasets)
                                            }
                                            sx={{
                                                flexShrink: 0,
                                                [`&.${tabClasses.disabled}`]: {
                                                    [`&.${tabClasses.tonal}`]: {
                                                        backgroundColor: (
                                                            theme
                                                        ) =>
                                                            theme.palette
                                                                .background
                                                                .main,
                                                    },
                                                },
                                            }}
                                        >
                                            {startCase(tab)}
                                        </Tab>
                                    </span>
                                </Tooltip>
                            ))}
                        </TabGroup>
                        {hasFilters && (
                            <Stack
                                flexDirection="row"
                                alignItems="center"
                                gap={2}
                            >
                                <DataGridDivider />
                                <FilterAppliedChip
                                    datasets={filteredDatasets}
                                    updateDataset={updateDataset}
                                />
                            </Stack>
                        )}
                        <MapFeedbackContainer
                            deletePolygon={deletePolygon}
                            setNavTab={setNavTab}
                        />
                    </HorizontalScroller>
                </Stack>
            </Grid>
        </Grid>
    );
};

export default MapTopbar;
